import { FC } from 'react';
import * as React from 'react';
import { Button, IconButton, TablePagination, TextField, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { useRouteMatch, Link } from 'react-router-dom';
import { notUndefined } from 'mir-utils/utils/utilities';
import { useTranslation } from 'mir-utils/i18n';
import { CustomPaginationActions } from './CustomPaginationActions';
import { MenuSelect } from './MenuSelect';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'grid',
      gridTemplateColumns: 'auto auto',
      alignItems: 'center',
    },
    actionBlock: {
      display: 'flex',
      justifyContent: 'flex-end',
    },
    actionsLeft: {
      display: 'flex',
      '& > * + *': {
        marginLeft: theme.spacing(2),
      },
    },
    actionButton: {
      whiteSpace: 'nowrap',
    },
  })
);

type Props = {
  search: string | null;
  setSearch: (search: string | null) => void;
  selectedColumns: Record<string, boolean>;
  setSelectedColumns: (
    value:
      | Record<string, boolean>
      | ((storedValue: Record<string, boolean>) => Record<string, boolean>)
  ) => void;
  selectColumn?:
    | {
        requiredColumns?: string[];
        hideColumn?: string[];
      }
    | boolean;
  columnTitles: Record<string, string>;
  searchEnabled: boolean | undefined;
  create?: boolean | JSX.Element;
  hasPagination: boolean;
  pagination?: number[] | boolean;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  setCurrentPageSize: React.Dispatch<React.SetStateAction<number>>;
  countElementsBeforePagination: number;
  processedDataLength: number;
  currentPageSize: number;
  currentPage: number;
};

/**
 * @summary Toolbar с выбором столбцов и поиском.
 *
 * @param search - Есть 3 состояния:
 *  null - поиск не идёт и кнопка начала поиска не отображается, значит поиск полностью отключен.
 *  '' - поиск начался, но поле ввода пустое.
 *  'string' - идёт поиск.
 * @param setSelectedColumns - Сохраняет в localStorage.
 * @param setSearch - SetState для установки значение поиска.
 * @param searchEnabled - Включен ли поиск.
 * @param columnTitles - Key - columnName и value - headtitle.
 * @param selectColumn - True включает выбор столбцов, обьект с параметрами requiredColumns и(или) hideColumn так же включает выбор стобцов.
 */
export const TableToolbar: FC<Props> = ({
  search,
  setSearch,
  searchEnabled,
  selectedColumns,
  setSelectedColumns,
  columnTitles,
  selectColumn,
  create,
  hasPagination,
  pagination,
  setCurrentPage,
  setCurrentPageSize,
  countElementsBeforePagination,
  processedDataLength,
  currentPageSize,
  currentPage,
}) => {
  const classes = useStyles();
  const { path } = useRouteMatch();

  const { t } = useTranslation();

  const tableHasToolbarItems = processedDataLength > 0;
  const tableHasSearch = tableHasToolbarItems && searchEnabled;
  const tableHasPagination = tableHasToolbarItems && hasPagination;
  const tableHasColumnSelection = tableHasToolbarItems && selectColumn;

  return (
    <div className={classes.root}>
      <div className={classes.actionsLeft}>
        {create &&
          (create === true ? (
            <Tooltip title={t('Add')}>
              <Button
                variant="contained"
                aria-label="add"
                color="secondary"
                size="medium"
                component={Link}
                to={`${path}/create`}
                startIcon={<AddIcon />}
              >
                {t('Add')}
              </Button>
            </Tooltip>
          ) : (
            create
          ))}
      </div>
      <div className={classes.actionBlock}>
        {tableHasPagination && (
          <TablePagination
            component="div"
            rowsPerPageOptions={Array.isArray(pagination) ? pagination : [5, 10, 25]}
            count={hasPagination ? countElementsBeforePagination : processedDataLength}
            rowsPerPage={currentPageSize}
            page={currentPage}
            onPageChange={(_event, newPage) => setCurrentPage(newPage)}
            onRowsPerPageChange={(event) => {
              setCurrentPage(0);
              setCurrentPageSize(Number(event.target.value));
            }}
            labelDisplayedRows={({ from, to, count }) =>
              t('{{from}}d-{{to}}d of {{number}}d', { from, to, number: count })
            }
            labelRowsPerPage={t('Rows per page:')}
            ActionsComponent={CustomPaginationActions}
          />
        )}
        {search !== null && (
          <TextField
            id="search-field"
            value={search}
            autoFocus
            variant="outlined"
            size="small"
            InputProps={{
              endAdornment: (
                <IconButton onClick={() => setSearch(null)} size="small">
                  <CloseIcon />
                </IconButton>
              ),
            }}
            onKeyDown={(event) => event.keyCode === 27 && setSearch(null)}
            onChange={(event) => {
              setSearch(event.target.value);
              setCurrentPage(0);
            }}
          />
        )}
        {tableHasSearch && search === null && (
          <div>
            <Tooltip title={t('Search')}>
              <IconButton onClick={() => search === null && setSearch('')} size="large">
                <SearchIcon />
              </IconButton>
            </Tooltip>
          </div>
        )}
        {tableHasColumnSelection && (
          <MenuSelect
            titles={
              typeof selectColumn !== 'boolean' &&
              selectColumn !== undefined &&
              selectColumn.requiredColumns !== undefined
                ? Object.fromEntries(
                    Object.entries(columnTitles).filter(
                      ([key]) => !notUndefined(selectColumn.requiredColumns).includes(key)
                    )
                  )
                : columnTitles
            }
            selectedColumns={selectedColumns}
            setSelectedColumns={setSelectedColumns}
          />
        )}
      </div>
    </div>
  );
};
